export const useUtmTracking = () => {
  const STORAGE_KEY_UTM_SOURCE = 'utm_source';
  const STORAGE_KEY_UTM_MEDIUM = 'utm_medium';
  const STORAGE_KEY_UTM_CAMPAIGN = 'utm_campaign';

  /**
   * Get UTM parameters from URL query string
   */
  const getUtmFromUrl = () => {
    if (typeof window === 'undefined') return { utm_source: null, utm_medium: null, utm_campaign: null };
    
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign')
    };
  };

  /**
   * Store UTM parameters in sessionStorage
   */
  const storeUtmParams = (utm_source: string | null, utm_medium: string | null, utm_campaign: string | null) => {
    if (typeof window === 'undefined') return;
    
    if (utm_source) {
      sessionStorage.setItem(STORAGE_KEY_UTM_SOURCE, utm_source);
    }
    if (utm_medium) {
      sessionStorage.setItem(STORAGE_KEY_UTM_MEDIUM, utm_medium);
    }
    if (utm_campaign) {
      sessionStorage.setItem(STORAGE_KEY_UTM_CAMPAIGN, utm_campaign);
    }
  };

  /**
   * Get stored UTM parameters from sessionStorage
   */
  const getStoredUtmParams = () => {
    if (typeof window === 'undefined') return { utm_source: null, utm_medium: null, utm_campaign: null };
    
    try {
      return {
        utm_source: sessionStorage.getItem(STORAGE_KEY_UTM_SOURCE),
        utm_medium: sessionStorage.getItem(STORAGE_KEY_UTM_MEDIUM),
        utm_campaign: sessionStorage.getItem(STORAGE_KEY_UTM_CAMPAIGN)
      };
    } catch (error) {
      console.warn('Error reading UTM params from sessionStorage:', error);
      return { utm_source: null, utm_medium: null, utm_campaign: null };
    }
  };

  /**
   * Build redirect URL with UTM parameters, current page URL, and CTA name
   * @param baseUrl - The base URL to redirect to
   * @param currentPagePath - Optional current page path
   * @param ctaName - Optional CTA name to track which button was clicked
   */
  const buildRedirectUrl = (baseUrl: string, currentPagePath?: string, ctaName?: string) => {
    if (typeof window === 'undefined') return baseUrl;
    
    try {
      const storedParams = getStoredUtmParams();
      const url = new URL(baseUrl);
      
      // Add stored UTM parameters (only if they exist)
      if (storedParams.utm_source) {
        url.searchParams.set('utm_source', storedParams.utm_source);
      }
      if (storedParams.utm_medium) {
        url.searchParams.set('utm_medium', storedParams.utm_medium);
      }
      if (storedParams.utm_campaign) {
        url.searchParams.set('utm_campaign', storedParams.utm_campaign);
      }
      
      // Add current page URL as a parameter (includes full URL with query params)
      const currentPageUrl = currentPagePath 
        ? `${window.location.origin}${currentPagePath}${window.location.search}`
        : window.location.href;
      url.searchParams.set('source_url', currentPageUrl);
      
      // Add CTA name to track which button was clicked
      if (ctaName) {
        url.searchParams.set('cta', ctaName);
      }
      
      // Carry the journey_id across the domain boundary via localStorage
      const jid = localStorage.getItem('analytics_journey_id')
      if (jid) {
        url.searchParams.set('journey_id', jid)
      }
      
      return url.toString();
    } catch (error) {
      console.warn('Error building redirect URL:', error);
      return baseUrl;
    }
  };

  /**
   * Initialize: Capture UTM params from URL and store them
   * Only updates if new values are present in URL (preserves existing stored values)
   * This ensures UTM params persist across internal navigation
   */
  const initializeUtmTracking = () => {
    if (typeof window === 'undefined') return;
    
    try {
      const urlParams = getUtmFromUrl();
      
      // Only store if they exist in URL (preserves existing stored values if URL doesn't have them)
      // This ensures UTM params persist across internal navigation
      if (urlParams.utm_source) {
        sessionStorage.setItem(STORAGE_KEY_UTM_SOURCE, urlParams.utm_source);
      }
      if (urlParams.utm_medium) {
        sessionStorage.setItem(STORAGE_KEY_UTM_MEDIUM, urlParams.utm_medium);
      }
      if (urlParams.utm_campaign) {
        sessionStorage.setItem(STORAGE_KEY_UTM_CAMPAIGN, urlParams.utm_campaign);
      }
    } catch (error) {
      // Silently handle errors (e.g., if sessionStorage is not available)
      console.warn('UTM tracking initialization error:', error);
    }
  };

  return {
    getUtmFromUrl,
    storeUtmParams,
    getStoredUtmParams,
    buildRedirectUrl,
    initializeUtmTracking
  };
};

